import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { OverdueProvider } from "./contexts/OverdueContext";
import { SupportNotifProvider } from "./contexts/SupportNotifContext";

// Pages
import AuthPage from "./pages/Auth/AuthPage";
import ResetPasswordPage from "./pages/Auth/ResetPasswordPage";
import MainLayout from "./components/Layout/MainLayout";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import FormBuilderPage from "./pages/FormBuilder/FormBuilderPage";
import OrdersPage, { NewOrderPage, OrderDetailPage } from "./pages/Orders/OrdersPage";
import CustomersPage from "./pages/Customers/CustomersPage";
import MaterialsPage from "./pages/Materials/MaterialsPage";
import TasksPage from "./pages/Tasks/TasksPage";
import ReportsPage from "./pages/Reports/ReportsPage";
import FinancePage from "./pages/Finance/FinancePage";
import SettingsPage from "./pages/Settings/SettingsPage";
import CalendarPage from "./pages/Calendar/CalendarPage";
import SupportPage from "./pages/Support/SupportPage";
import AdminPage from "./pages/Admin/AdminPage";
import PricingPage from "./pages/Pricing/PricingPage";
import HelpPage from "./pages/Help/HelpPage";
import MyPageEditor from "./pages/MyPage/MyPageEditor";
import PublicPage from "./pages/MyPage/PublicPage";
import SubscriptionTermsPage from "./pages/Legal/SubscriptionTermsPage";
import PersonalDataPolicyPage from "./pages/Legal/PersonalDataPolicyPage";
import InvoicesPage from "./pages/Invoices/InvoicesPage";
import PublicInvoicePage from "./pages/Invoices/PublicInvoicePage";
import { PaymentSuccessPage, PaymentFailPage } from "./pages/Payment/PaymentResultPage";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route Component (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-muted-foreground">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      
      {/* Public Legal Pages */}
      <Route path="/subscription-terms" element={<SubscriptionTermsPage />} />
      <Route path="/personal-data-policy" element={<PersonalDataPolicyPage />} />
      <Route path="/invoice/:publicId" element={<PublicInvoicePage />} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/form-builder" element={<FormBuilderPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/orders/new" element={<NewOrderPage />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/materials" element={<MaterialsPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/reports" element={<ReportsPage />} />
                <Route path="/finance" element={<FinancePage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/fail" element={<PaymentFailPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/my-page" element={<MyPageEditor />} />
      </Route>

      {/* Public page (slug) - must be BEFORE catch-all, AFTER all static routes */}
      <Route path="/:slug" element={<LightWrapper><PublicPage /></LightWrapper>} />
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* All routes - with theme */}
        <Route path="*" element={
          <ThemeProvider>
            <AuthProvider>
              <OverdueProvider>
              <SupportNotifProvider>
                <AppRoutes />
                <Toaster 
                  position="bottom-right" 
                  richColors 
                  closeButton
                  toastOptions={{
                    style: {
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      color: 'hsl(var(--card-foreground))',
                    },
                  }}
                />
              </SupportNotifProvider>
              </OverdueProvider>
            </AuthProvider>
          </ThemeProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

function LightWrapper({ children }) {
  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    root.classList.add('light');
  }, []);
  return children;
}

export default App;
